import { ApiError, type Api } from "./api";
import { saysKeyword } from "../keywords";
import { clearTo, type Store, type ChannelState } from "./store";
import type { Attachment, Bookmark, Huddle, Channel, ChannelEvent, Frame, Membership, Message, ModalView, PersonalEvent, Principal, Reaction, Reminder, SidebarLayout, Task, UUID, Welcome } from "./types";

// SocketLike is the part of WebSocket the engine uses, so tests can fake it.
export interface SocketLike {
  send(data: string): void;
  close(code?: number, reason?: string): void;
  onopen: (() => void) | null;
  onmessage: ((ev: { data: string }) => void) | null;
  onclose: ((ev: { code: number }) => void) | null;
}

export type SocketFactory = (url: string, protocol: string) => SocketLike;

export interface SendOptions {
  threadRootID?: UUID | null;
  fileIDs?: UUID[];
  // clientMsgID is set when retrying, so the server sees the same request.
  clientMsgID?: string;
  // alsoInChannel sends a thread reply to the channel too.
  alsoInChannel?: boolean;
}

export type ApiLike = Pick<Api, "getToken" | "listChannels" | "getChannel" | "events" | "history" | "message" | "thread" | "post" | "markRead" | "markUnread" | "getTask">;

export interface EngineOptions {
  url: string;
  socket: SocketFactory;
  // maxLive is how many channels to subscribe to (default 300).
  maxLive?: number;
  random?: () => number;
  schedule?: (fn: () => void, ms: number) => unknown;
  now?: () => number;
  log?: (msg: string, ...args: unknown[]) => void;
  // notify is called for a live message that mentions the user or arrives in
  // one of their DMs. It is not called for messages read while catching up.
  notify?: (m: Message, channel: Channel) => void;
  // profile is called when someone in the workspace changed their name,
  // status or away, to look them up again.
  // profile hears someone was made or changed, with them as they are now.
  profile?: (principalID: UUID, principal?: Principal) => void;
  // people hears many were made or changed at once, as a directory sync does.
  people?: (principals: Principal[]) => void;
  // workspace is called when the workspace changed, as when renamed.
  workspace?: () => void;
  // recalled is called when a message was recalled, to take down its
  // notification.
  recalled?: (id: UUID) => void;
  // reminder is called when one of the user's reminders comes due.
  reminder?: (r: Reminder) => void;
  // scheduled is called when the user's messages to send later changed.
  scheduled?: () => void;
  // preferences hears the user's preferences changed, on any device.
  preferences?: () => void;
  // huddle is told a channel's call changed, with how it was before; now
  // is undefined once it has ended.
  huddle?: (channel: ChannelState, now: Huddle | undefined, before: Huddle | undefined) => void;
  // huddleDeclined is told someone rung for a channel's call declined it.
  huddleDeclined?: (channel: UUID, who: UUID) => void;
}

// Close codes that mean "do not reconnect until something changes".
const fatalCodes = new Set([4001, 4003, 4010, 4013]);
const maxSubscribeBatch = 100;
// The engine subscribes to at most this many channels, under the server's
// 500, and hears of the rest as channel.activity (sync spec section 6.5).
const defaultMaxLive = 300;
// maxPresenceWatch is the server's default limit on watched principals.
const maxPresenceWatch = 500;
const typingShowMs = 5000;
const typingSendMs = 3000;

interface Sync {
  // Events that arrived early, by seq, waiting for the gap before them.
  buffer: Map<number, ChannelEvent>;
  // fetching is set while a sync_required range is read over HTTP.
  fetching: boolean;
  resubscribing: boolean;
}

// SyncEngine keeps the store in step with the server, following the sync
// protocol (docs/specs/sync.md): it resumes the personal stream by cursor,
// applies channel events strictly in seq order, re-subscribes on a gap, and
// fills sync_required ranges over HTTP.
export class SyncEngine {
  private ws: SocketLike | null = null;
  private cursor: string | null = null;
  private attempt = 0;
  private stopped = false;
  private refs = 0;
  private seenPersonal: string[] = [];
  // Tasks a card asked for, loaded or not.
  private readonly wantedTasks = new Set<UUID>();
  private syncs = new Map<UUID, Sync>();
  private lastTyping = new Map<UUID, number>();
  private watched: UUID[] = [];
  // live is the channels subscribed to; recent those opened lately, which
  // stay live.
  private live = new Set<UUID>();
  private recent: UUID[] = [];
  // earlyDeltas is message.delta text that came before its message, kept
  // a few seconds for when the message arrives.
  private earlyDeltas = new Map<UUID, { at: number; deltas: Delta[] }>();
  // countedTo is, for channels whose counts came from the server while not
  // subscribed, the seq those counts include: replayed messages up to it
  // are not counted again.
  private countedTo = new Map<UUID, number>();
  private readonly maxLive: number;
  private readonly random: () => number;
  private readonly schedule: (fn: () => void, ms: number) => unknown;
  private readonly now: () => number;
  private readonly log: (msg: string, ...args: unknown[]) => void;

  constructor(
    private readonly api: ApiLike,
    private readonly store: Store,
    private readonly opts: EngineOptions,
  ) {
    this.random = opts.random ?? Math.random;
    this.schedule = opts.schedule ?? ((fn, ms) => setTimeout(fn, ms));
    this.now = opts.now ?? Date.now;
    this.log = opts.log ?? (() => {});
    this.maxLive = opts.maxLive ?? defaultMaxLive;
  }

  async start() {
    this.stopped = false;
    await this.fullResync();
    this.connect();
  }

  stop() {
    this.stopped = true;
    this.ws?.close(1000);
    this.ws = null;
  }

  private me(): UUID | undefined {
    return this.store.getSnapshot().me?.id;
  }

  // ---- connection ----

  private connect() {
    if (this.stopped) return;
    this.store.update((s) => (s.connection = "connecting"));
    const ws = this.opts.socket(this.opts.url, "lantern.v1");
    this.ws = ws;
    ws.onopen = () =>
      this.sendFrame("hello", {
        protocol: 1,
        token: this.api.getToken(),
        client: { name: "lantern-web", version: "0.1.0", device_id: deviceID() },
        cursor: this.cursor,
      });
    ws.onmessage = (ev) => {
      let f: Frame;
      try {
        f = JSON.parse(ev.data);
      } catch {
        return;
      }
      void this.handle(f);
    };
    ws.onclose = (ev) => {
      if (this.ws !== ws) return;
      this.ws = null;
      if (this.stopped) return;
      if (fatalCodes.has(ev.code)) {
        this.store.update((s) => (s.connection = "signed_out"));
        return;
      }
      this.store.update((s) => (s.connection = "offline"));
      this.reconnectLater(0);
    };
  }

  // reconnectLater waits random(0, min(30 s, 1 s × 2^attempt)), and at least
  // minMs (sync spec section 5.4).
  private reconnectLater(minMs: number) {
    const cap = Math.min(30_000, 1000 * 2 ** this.attempt);
    this.attempt++;
    this.schedule(() => this.connect(), minMs + this.random() * cap);
  }

  private sendFrame(op: string, d?: unknown, ref?: string) {
    this.ws?.send(JSON.stringify(ref ? { op, ref, d } : { op, d }));
  }

  private async handle(f: Frame) {
    switch (f.op) {
      case "welcome":
        return this.onWelcome(f.d as Welcome);
      case "ping":
        return this.sendFrame("pong", f.d);
      case "event":
        return this.onEvent(f.d as ChannelEvent & PersonalEvent);
      case "ephemeral":
        return this.onEphemeral(f.d as { type: string; channel_id: UUID; principal_id: UUID });
      case "sync_required": {
        const d = f.d as { channel_id: UUID; live_from_seq: number };
        return this.fillRange(d.channel_id, d.live_from_seq);
      }
      case "ok":
        return this.onOK(f);
      case "reconnect": {
        const d = f.d as { retry_after_ms?: number };
        const ws = this.ws;
        this.ws = null;
        ws?.close(1000);
        this.store.update((s) => (s.connection = "offline"));
        return this.reconnectLater(d?.retry_after_ms ?? 1000);
      }
      case "error":
        return this.log("sync error", f.d);
    }
  }

  // serverNow is the server's clock, as of the last welcome; this
  // browser's own may be off.
  serverNow() {
    return this.now() + this.clockOffset;
  }
  private clockOffset = 0;

  private async onWelcome(w: Welcome) {
    this.attempt = 0;
    if (w.server_time) this.clockOffset = Date.parse(w.server_time) - this.now();
    if (w.cursor_reset && this.cursor !== null) {
      // The server cannot resume our cursor, so start again from a snapshot.
      await this.fullResync();
    } else if (this.cursor === null) {
      this.cursor = w.head_cursor;
    }
    this.store.update((s) => (s.connection = "online"));
    this.live.clear();
    this.subscribe(this.pickLive());
    // Channels not subscribed to missed what happened while away; their
    // counts are read again.
    if (!w.cursor_reset && this.store.getSnapshot().channels.size > this.live.size) void this.refreshCounts();
    if (this.watched.length) this.sendWatch();
    this.loadOpen();
  }

  // recount reads one channel's unread counts again, keeping them only if
  // nothing came in meanwhile, which the counts here already took in.
  private async recount(id: UUID) {
    try {
      const { channel } = await this.api.getChannel(id);
      this.store.update((s) => {
        const cs = s.channels.get(id);
        const m = cs?.channel.membership;
        if (!cs || !m || !channel.membership || channel.last_seq !== cs.lastSeq) return;
        Object.assign(m, { unread_count: channel.membership.unread_count, mention_count: channel.membership.mention_count });
      });
    } catch (err) {
      this.log("recount", id, err);
    }
  }

  // refreshCounts reads the unread counts and order of the channels not
  // subscribed to.
  private async refreshCounts() {
    try {
      const { channels } = await this.api.listChannels();
      this.store.update((s) => {
        for (const ch of channels) {
          const cs = s.channels.get(ch.id);
          // Not over counts newer than these, from activity meanwhile.
          if (!cs || this.live.has(ch.id) || ch.last_seq < (this.countedTo.get(ch.id) ?? 0)) continue;
          cs.channel.last_message_at = ch.last_message_at;
          if (cs.channel.membership && ch.membership) Object.assign(cs.channel.membership, { unread_count: ch.membership.unread_count, mention_count: ch.membership.mention_count });
          this.countedTo.set(ch.id, ch.last_seq);
        }
      });
    } catch (err) {
      this.log("refresh counts", err);
    }
  }

  // fullResync reads every membership and the head cursor it is consistent
  // with (sync spec section 6.4).
  private async fullResync() {
    const { channels, head_cursor } = await this.api.listChannels();
    this.cursor = head_cursor ?? null;
    this.store.update((s) => {
      const keep = new Set(channels.map((c) => c.id));
      for (const id of s.channels.keys()) if (!keep.has(id)) s.channels.delete(id);
      for (const ch of channels) {
        const cs = s.channels.get(ch.id);
        if (cs) {
          cs.channel = ch;
          cs.lastSeq = Math.max(cs.lastSeq, ch.last_seq);
        } else {
          s.channels.set(ch.id, newChannelState(ch));
        }
      }
    });
    this.loadOpen();
  }

  // pickLive chooses the channels to subscribe to: those opened lately,
  // then direct messages, then the most recently active.
  private pickLive(): UUID[] {
    const recent = new Set(this.recent);
    const rank = (c: Channel) => (recent.has(c.id) ? 0 : c.kind === "dm" || c.kind === "group_dm" ? 1 : 2);
    const chans = [...this.store.getSnapshot().channels.values()].map((cs) => cs.channel);
    chans.sort((a, b) => rank(a) - rank(b) || (b.last_message_at ?? "").localeCompare(a.last_message_at ?? ""));
    return chans.slice(0, this.maxLive).map((c) => c.id);
  }

  // ensureLive subscribes to a channel, if it is not already, making room
  // by letting go of the least recently active one not opened lately.
  private ensureLive(id: UUID) {
    if (this.live.has(id)) return;
    this.subscribe([id]);
    if (this.live.size <= this.maxLive) return;
    const recent = new Set([id, ...this.recent]);
    const channels = this.store.getSnapshot().channels;
    let out: Channel | undefined;
    for (const live of this.live) {
      const c = channels.get(live)?.channel;
      if (!c || recent.has(live)) continue;
      if (!out || (c.last_message_at ?? "") < (out.last_message_at ?? "")) out = c;
    }
    if (!out) return;
    this.live.delete(out.id);
    this.sendFrame("unsubscribe", { channels: [out.id] }, "unsub" + ++this.refs);
  }

  private subscribe(ids: UUID[]) {
    for (const id of ids) this.live.add(id);
    const s = this.store.getSnapshot();
    for (let i = 0; i < ids.length; i += maxSubscribeBatch) {
      const channels = ids.slice(i, i + maxSubscribeBatch).map((id) => ({
        channel_id: id,
        since_seq: s.channels.get(id)?.lastSeq ?? 0,
      }));
      this.sendFrame("subscribe", { channels }, "sub" + ++this.refs);
    }
  }

  private sendWatch() {
    this.sendFrame("presence_subscribe", { principal_ids: this.watched }, "pres" + ++this.refs);
  }

  private onOK(f: Frame) {
    if (f.ref?.startsWith("pres")) {
      // The reply lists who of the watched is online now.
      const online = (f.d as { presence?: Record<UUID, string> } | undefined)?.presence ?? {};
      this.store.update((s) => {
        for (const id of this.watched) {
          if (online[id]) s.presence.set(id, online[id]);
          else s.presence.delete(id);
        }
      });
      return;
    }
    const d = f.d as { channels?: { channel_id: UUID; error?: string }[] } | undefined;
    for (const c of d?.channels ?? []) {
      const sync = this.syncs.get(c.channel_id);
      if (sync) sync.resubscribing = false;
      if (c.error) this.live.delete(c.channel_id);
      if (c.error === "forbidden") this.store.update((s) => s.channels.delete(c.channel_id));
    }
  }

  // ---- events ----

  private sync(id: UUID): Sync {
    let s = this.syncs.get(id);
    if (!s) {
      s = { buffer: new Map(), fetching: false, resubscribing: false };
      this.syncs.set(id, s);
    }
    return s;
  }

  private onEvent(ev: ChannelEvent & PersonalEvent) {
    if (ev.cursor !== undefined) return this.onPersonal(ev);
    const cs = this.store.getSnapshot().channels.get(ev.channel_id);
    if (!cs || ev.seq <= cs.lastSeq) return;
    const sync = this.sync(ev.channel_id);
    if (sync.fetching || ev.seq > cs.lastSeq + 1) {
      sync.buffer.set(ev.seq, ev);
      if (!sync.fetching && !sync.resubscribing) {
        // A gap: ask the server to replay from what we have (sync spec 8).
        sync.resubscribing = true;
        this.subscribe([ev.channel_id]);
      }
      return;
    }
    this.store.update((s) => {
      const c = s.channels.get(ev.channel_id);
      if (c) this.applyInOrder(c, ev, sync);
    });
    if (ev.type === "message.created") this.maybeNotify(ev.body as unknown as Message, cs.channel);
  }

  private maybeNotify(m: Message, channel: Channel) {
    const me = this.me();
    if (!this.opts.notify || !me || m.author.id === me || m.class !== "human") return;
    // As the member chose: every message, mentions only, or nothing; DMs
    // default to every message and channels to mentions.
    const dm = channel.kind === "dm" || channel.kind === "group_dm";
    const level = channel.membership?.notify_level ?? (dm ? "all" : "mentions");
    if (level === "none") return;
    if (level === "all" || m.mentions?.includes(me) || saysKeyword(m.text, this.store.getSnapshot().keywords)) this.opts.notify(m, channel);
  }

  // applyInOrder applies ev, which is next in seq, and then any buffered
  // events that follow it.
  private applyInOrder(cs: ChannelState, ev: ChannelEvent, sync: Sync) {
    let next: ChannelEvent | undefined = ev;
    while (next) {
      this.apply(cs, next);
      cs.lastSeq = next.seq;
      sync.buffer.delete(next.seq);
      next = sync.buffer.get(cs.lastSeq + 1);
    }
    for (const seq of sync.buffer.keys()) if (seq <= cs.lastSeq) sync.buffer.delete(seq);
  }

  // fillRange reads (lastSeq, liveFrom] over HTTP while live events wait in
  // the buffer, then applies both in order (sync spec section 6.3).
  private async fillRange(id: UUID, liveFrom: number) {
    const sync = this.sync(id);
    if (sync.fetching) return;
    sync.fetching = true;
    try {
      for (;;) {
        const cs = this.store.getSnapshot().channels.get(id);
        if (!cs || cs.lastSeq >= liveFrom) break;
        const page = await this.api.events(id, cs.lastSeq, 500);
        if (page.events.length === 0) break;
        this.store.update((s) => {
          const c = s.channels.get(id);
          if (!c) return;
          for (const ev of page.events) {
            if (ev.seq !== c.lastSeq + 1) continue;
            this.apply(c, ev);
            c.lastSeq = ev.seq;
          }
        });
      }
    } catch (err) {
      this.log("fill range", id, err);
    } finally {
      sync.fetching = false;
      this.store.update((s) => {
        const c = s.channels.get(id);
        if (!c) return;
        const first = sync.buffer.get(c.lastSeq + 1);
        if (first) this.applyInOrder(c, first, sync);
      });
    }
  }

  private apply(cs: ChannelState, ev: ChannelEvent) {
    const s = this.store.getSnapshot();
    const me = this.me();
    cs.channel.last_seq = Math.max(cs.channel.last_seq, ev.seq);
    const b = ev.body;
    switch (ev.type) {
      case "message.created": {
        const m = b as unknown as Message;
        if (m.client_msg_id && m.author.id === me) s.pending.delete(m.client_msg_id);
        if (m.thread_root_id) {
          const root = cs.messages.get(m.thread_root_id);
          if (root) {
            root.reply_count = (root.reply_count ?? 0) + 1;
            root.last_reply_seq = m.seq;
          }
          s.threads.get(m.thread_root_id)?.set(m.id, m);
          // Sent to the channel too, it shows there as well.
          if (m.also_in_channel && cs.loaded) cs.messages.set(m.id, m);
        } else if (cs.loaded) {
          cs.messages.set(m.id, m);
        }
        const early = this.earlyDeltas.get(m.id);
        if (early) {
          this.earlyDeltas.delete(m.id);
          patchMessage(cs, s.threads, m.id, (x) => early.deltas.forEach((d) => applyDelta(x, d)));
        }
        cs.channel.last_message_at = m.created_at;
        const mem = cs.channel.membership;
        if (mem && m.author.id !== me && m.class === "human" && m.seq > mem.last_read_seq && m.seq > (this.countedTo.get(cs.channel.id) ?? 0)) {
          mem.unread_count = Math.min(100, mem.unread_count + 1);
          const mine = (me && m.mentions?.includes(me)) || (mem.notify_level !== "none" && saysKeyword(m.text, s.keywords));
          if (mine) mem.mention_count = Math.min(100, mem.mention_count + 1);
        }
        typingStopped(s.typing, cs.channel.id, m.author.id);
        return;
      }
      case "message.updated": {
        const id = b.message_id as UUID;
        const recalled = typeof b.recalled_at === "string";
        if (recalled) {
          // A recalled message no longer counts as unread or as a mention,
          // and its notification is taken down. The counts are taken off
          // here when the message is at hand, then read again, since it may
          // not be (or have mentioned a group one is in).
          const old = cs.messages.get(id) ?? findReply(s.threads, id);
          const mem = cs.channel.membership;
          if (old && !old.recalled_at && mem && old.author.id !== me && old.class === "human" && old.seq > mem.last_read_seq) {
            mem.unread_count = Math.max(0, mem.unread_count - 1);
            const mine = (me && old.mentions?.includes(me)) || (mem.notify_level !== "none" && saysKeyword(old.text, s.keywords));
            if (mine) mem.mention_count = Math.max(0, mem.mention_count - 1);
          }
          if (mem && ev.seq > mem.last_read_seq) void this.recount(cs.channel.id);
          this.opts.recalled?.(id);
        }
        patchMessage(cs, s.threads, id, (m) => {
          if (typeof b.text === "string") m.text = b.text;
          if ("payload" in b) m.payload = b.payload;
          if (typeof b.state === "string") m.state = b.state as Message["state"];
          if (Array.isArray(b.mentions)) m.mentions = b.mentions as UUID[];
          if (Array.isArray(b.files)) m.files = b.files as Attachment[];
          if (typeof b.edited_at === "string") m.edited_at = b.edited_at;
          if (recalled) Object.assign(m, { recalled_at: b.recalled_at as string, recalled_by: b.recalled_by as UUID, reactions: [] });
        });
        return;
      }
      case "message.deleted": {
        const id = b.message_id as UUID;
        const m = cs.messages.get(id) ?? findReply(s.threads, id);
        if (m?.thread_root_id) {
          const root = cs.messages.get(m.thread_root_id);
          if (root?.reply_count) root.reply_count--;
          s.threads.get(m.thread_root_id)?.delete(id);
        }
        cs.messages.delete(id);
        return;
      }
      case "reaction.added":
      case "reaction.removed":
        patchMessage(cs, s.threads, b.message_id as UUID, (m) => {
          m.reactions = applyReaction(m.reactions ?? [], b.emoji as string, b.principal_id as UUID, ev.type === "reaction.added");
        });
        return;
      case "pin.added":
      case "pin.removed": {
        // Only channels whose pins were loaded keep them.
        const pins = s.pins.get(cs.channel.id);
        if (!pins) return;
        if (ev.type === "pin.added") pins.set(b.message_id as UUID, b.principal_id as UUID);
        else pins.delete(b.message_id as UUID);
        return;
      }
      case "bookmark.added":
      case "bookmark.updated":
      case "bookmark.removed": {
        // Only channels whose bookmarks were loaded keep them.
        const list = s.bookmarks.get(cs.channel.id);
        if (!list) return;
        const bm = b.bookmark as Bookmark | undefined;
        const id = bm?.id ?? (b.bookmark_id as UUID);
        const rest = list.filter((x) => x.id !== id);
        if (ev.type === "bookmark.removed" || !bm) s.bookmarks.set(cs.channel.id, rest);
        else if (ev.type === "bookmark.added") s.bookmarks.set(cs.channel.id, [...rest, bm]);
        else s.bookmarks.set(cs.channel.id, list.map((x) => (x.id === id ? bm : x)));
        return;
      }
      case "huddle.updated": {
        const h = b.huddle as Huddle;
        this.opts.huddle?.(cs, h, s.huddles.get(cs.channel.id));
        s.huddles.set(cs.channel.id, h);
        return;
      }
      case "huddle.ended":
        this.opts.huddle?.(cs, undefined, s.huddles.get(cs.channel.id));
        s.huddles.delete(cs.channel.id);
        return;
      case "member.joined":
        cs.memberChanges = (cs.memberChanges ?? 0) + 1;
        return;
      case "member.left":
        if (b.principal_id === me) s.channels.delete(cs.channel.id);
        else cs.memberChanges = (cs.memberChanges ?? 0) + 1;
        return;
      case "channel.updated":
        Object.assign(cs.channel, b);
        return;
      case "task.updated": {
        // Patch a task on screen at once, then fetch the rest (lease,
        // output, input request) that the event does not carry.
        const t = s.tasks.get(b.task_id as UUID);
        if (!t) {
          // A thread's root message is posted just before its task exists,
          // so a card may still be waiting for it. One linked to a thread
          // that was opened shows under its root.
          const id = b.task_id as UUID;
          if (this.wantedTasks.has(id) || s.threads.has(b.thread_root_id as UUID)) queueMicrotask(() => void this.loadTask(id));
          return;
        }
        t.state = b.state as Task["state"];
        if ("progress" in b) t.progress = b.progress as Task["progress"];
        if (b.children) t.children = b.children as Task["children"];
        queueMicrotask(() => void this.loadTask(t.id));
        return;
      }
    }
  }

  private seen(id: string) {
    if (this.seenPersonal.includes(id)) return true;
    this.seenPersonal.push(id);
    if (this.seenPersonal.length > 1000) this.seenPersonal.shift();
    return false;
  }

  private async onPersonal(ev: PersonalEvent) {
    if (this.seen(ev.id)) return;
    this.cursor = ev.cursor;
    const b = ev.body;
    switch (ev.type) {
      case "channel.added": {
        const id = (b as unknown as Channel).id;
        try {
          const { channel } = await this.api.getChannel(id);
          this.addChannel(channel);
        } catch (err) {
          this.log("channel.added", id, err);
        }
        return;
      }
      case "channel.removed":
        this.live.delete(b.channel_id as UUID);
        this.store.update((s) => s.channels.delete(b.channel_id as UUID));
        return;
      case "task.assigned":
      case "task.updated": {
        const id = b.task_id as UUID;
        this.store.update((s) => s.taskNotices++);
        if (this.store.getSnapshot().tasks.has(id)) await this.loadTask(id);
        return;
      }
      case "saved.updated":
        this.store.update((s) => {
          if (b.saved) s.saved.add(b.message_id as UUID);
          else s.saved.delete(b.message_id as UUID);
          s.savedNotices++;
        });
        return;
      case "reminder.due":
        this.opts.reminder?.(b as unknown as Reminder);
        return;
      case "scheduled.updated":
        this.opts.scheduled?.();
        return;
      case "prefs.updated":
        // Looked up rather than taken from the event, so two changes close
        // together end on the latest, whichever event comes last.
        this.opts.preferences?.();
        return;
      case "sidebar.updated":
        this.store.update((s) => {
          const l = b as unknown as SidebarLayout;
          s.sidebar = l.sections;
          s.sidebarSort = l.sort ?? "name";
          s.sidebarShow = l.show ?? "all";
          s.sidebarShowArchived = !!l.show_archived;
        });
        return;
      case "thread.activity":
        this.store.update((s) => s.threadNotices++);
        return;
      case "password.requests":
        this.store.update((s) => s.passwordRequestNotices++);
        return;
      case "mention": {
        // A live channel's own event notifies; in one heard of only as
        // activity, the message is looked up.
        const ch = b.channel_id as UUID;
        const cs = this.store.getSnapshot().channels.get(ch);
        if (this.live.has(ch) || !cs) return;
        // Not for mentions caught up on after a reconnect, or read already.
        const stale = ev.ts && this.serverNow() - Date.parse(ev.ts) > 60_000;
        if (stale || (cs.channel.membership?.last_read_seq ?? 0) >= (b.seq as number)) return;
        try {
          const { message } = await this.api.message(ch, b.message_id as UUID);
          this.maybeNotify(message, cs.channel);
        } catch (err) {
          this.log("mention", ch, err);
        }
        return;
      }
      case "membership.updated":
        this.store.update((s) => {
          const m = s.channels.get(b.channel_id as UUID)?.channel.membership;
          if (!m) return;
          if ("notify_level" in b) m.notify_level = b.notify_level as Membership["notify_level"];
          if ("closed_at" in b) m.closed_at = b.closed_at as string | null;
          if (typeof b.cleared_seq === "number") clearTo(s, b.channel_id as UUID, b.cleared_seq);
        });
        return;
      case "channel.read": {
        const id = b.channel_id as UUID;
        const seq = b.last_read_seq as number;
        const cs = this.store.getSnapshot().channels.get(id);
        if (!cs?.channel.membership) return;
        if (seq >= cs.lastSeq) {
          this.store.update((s) => {
            const m = s.channels.get(id)?.channel.membership;
            if (m) Object.assign(m, { last_read_seq: seq, unread_count: 0, mention_count: 0 });
          });
          return;
        }
        // Read part of the way on another device: ask for the counts.
        try {
          const { channel } = await this.api.getChannel(id);
          this.store.update((s) => {
            const c = s.channels.get(id);
            if (c && channel.membership) c.channel.membership = channel.membership;
          });
        } catch (err) {
          this.log("channel.read", id, err);
        }
        return;
      }
    }
  }

  private onEphemeral(e: { type: string; channel_id: UUID; principal_id: UUID; status?: string; principal?: Principal; principals?: Principal[] }) {
    if (e.type === "presence") {
      this.store.update((s) => {
        if (e.status === "offline" || !e.status) s.presence.delete(e.principal_id);
        else s.presence.set(e.principal_id, e.status);
      });
      return;
    }
    if (e.type === "profile") {
      this.opts.profile?.(e.principal_id, e.principal);
      return;
    }
    if (e.type === "people") {
      this.opts.people?.(e.principals ?? []);
      return;
    }
    if (e.type === "workspace") {
      this.opts.workspace?.();
      return;
    }
    if (e.type === "view.open" || e.type === "view.push" || e.type === "view.update") {
      const v = (e as unknown as { view: ModalView }).view;
      this.store.update((s) => {
        if (e.type === "view.open") s.modals = [v];
        else if (e.type === "view.push") s.modals = [...s.modals.filter((m) => m.id !== v.id), v];
        else s.modals = s.modals.map((m) => (m.id === v.id ? v : m));
      });
      return;
    }
    if (e.type === "artifact.updated") {
      this.store.update((s) => s.artifactBumps.set(e.channel_id, (s.artifactBumps.get(e.channel_id) ?? 0) + 1));
      return;
    }
    if (e.type === "message") {
      const m = (e as unknown as { message: Message }).message;
      this.store.update((s) => s.ephemeral.set(m.channel_id, [...(s.ephemeral.get(m.channel_id) ?? []), m].slice(-20)));
      return;
    }
    if (e.type === "channel.activity") {
      // A member channel not subscribed to: its counts, and that it moved.
      const a = e as unknown as { last_seq: number; unread_count: number; mention_count: number };
      this.store.update((s) => {
        const cs = s.channels.get(e.channel_id);
        const m = cs?.channel.membership;
        if (!cs || !m || a.last_seq <= cs.lastSeq || a.last_seq < (this.countedTo.get(e.channel_id) ?? 0)) return;
        if (a.unread_count > m.unread_count) cs.channel.last_message_at = new Date(this.serverNow()).toISOString();
        Object.assign(m, { unread_count: a.unread_count, mention_count: a.mention_count });
        this.countedTo.set(e.channel_id, a.last_seq);
      });
      return;
    }
    if (e.type === "message.delta") {
      // Text streamed into a streaming message: applied where it follows on
      // from what is shown, and the final text replaces it all.
      const d = e as unknown as Delta & { message_id: UUID };
      this.store.update((s) => {
        const cs = s.channels.get(e.channel_id);
        if (!cs) return;
        if (cs.messages.has(d.message_id) || [...s.threads.values()].some((t) => t.has(d.message_id))) {
          patchMessage(cs, s.threads, d.message_id, (m) => applyDelta(m, d));
          return;
        }
        // Deltas travel faster than the message's own event; kept only
        // where the message will be shown.
        if (!cs.loaded && s.threads.size === 0) return;
        const now = this.now();
        for (const [id, x] of this.earlyDeltas) if (now - x.at > 5000) this.earlyDeltas.delete(id);
        const early = this.earlyDeltas.get(d.message_id);
        if (early) {
          if (early.deltas.length < 200) early.deltas.push(d);
        } else if (this.earlyDeltas.size < 50) {
          this.earlyDeltas.set(d.message_id, { at: now, deltas: [d] });
        }
      });
      return;
    }
    if (e.type === "task.progress") {
      const p = e as unknown as { task_id: UUID; progress: Task["progress"] };
      this.store.update((s) => {
        const t = s.tasks.get(p.task_id);
        if (t) t.progress = p.progress;
      });
      return;
    }
    if (e.type === "huddle.declined") {
      this.opts.huddleDeclined?.(e.channel_id, e.principal_id);
      return;
    }
    if (e.type !== "typing") return;
    const until = this.now() + typingShowMs;
    this.store.update((s) => {
      let m = s.typing.get(e.channel_id);
      if (!m) s.typing.set(e.channel_id, (m = new Map()));
      m.set(e.principal_id, until);
    });
    // Nothing else may change on screen when it runs out, so take it down
    // then, unless a later typing event has moved the time on.
    this.schedule(() => {
      if ((this.store.getSnapshot().typing.get(e.channel_id)?.get(e.principal_id) ?? 0) > this.now()) return;
      this.store.update((s) => typingStopped(s.typing, e.channel_id, e.principal_id));
    }, typingShowMs + 50);
  }

  // ---- actions ----

  // loadTask fetches a task to show and keeps it in the store.
  async loadTask(id: UUID) {
    this.wantedTasks.add(id);
    try {
      const { task } = await this.api.getTask(id);
      this.store.update((s) => s.tasks.set(id, task));
    } catch (err) {
      this.log("task", id, err);
    }
  }

  // open is the channels shown, whose history is loaded until it is: when
  // one becomes known (just created or joined), after a failure, and after
  // a reconnect.
  private open = new Set<UUID>();
  private loading = new Set<UUID>();
  private retrying = new Set<UUID>();

  // openChannel loads a channel's recent history the first time it is shown.
  async openChannel(id: UUID) {
    this.open.add(id);
    this.recent = [id, ...this.recent.filter((r) => r !== id)].slice(0, 20);
    if (!this.store.getSnapshot().channels.has(id)) return;
    if (this.store.getSnapshot().connection === "online") this.ensureLive(id);
    await this.loadHistory(id);
  }

  // lookUp finds a channel shown before the engine heard of it, as one just
  // created, joined or opened as a DM, ahead of its channel.added: it is
  // kept if one is a member.
  async lookUp(id: UUID) {
    if (this.store.getSnapshot().channels.has(id)) return;
    try {
      const { channel } = await this.api.getChannel(id);
      if (channel.membership) this.addChannel(channel);
    } catch (err) {
      this.log("look up", id, err);
    }
  }

  private addChannel(channel: Channel) {
    const id = channel.id;
    this.store.update((s) => {
      if (!s.channels.has(id)) s.channels.set(id, newChannelState(channel));
    });
    this.ensureLive(id);
    void this.loadHistory(id);
  }

  // retryHistory tries loading a shown channel's history now, as when one
  // asks to after it failed.
  async retryHistory(id: UUID) {
    await this.loadHistory(id);
  }

  // closeChannel says a channel is no longer shown.
  closeChannel(id: UUID) {
    this.open.delete(id);
  }

  private async loadHistory(id: UUID, attempt = 0) {
    const cs = this.store.getSnapshot().channels.get(id);
    if (this.stopped || !cs || cs.loaded || !this.open.has(id) || this.loading.has(id)) return;
    this.loading.add(id);
    try {
      const { messages } = await this.api.history(id);
      this.store.update((s) => {
        const c = s.channels.get(id);
        if (!c) return;
        for (const m of messages) if (!c.messages.has(m.id)) c.messages.set(m.id, m);
        c.loaded = true;
        c.failed = false;
        c.hasMoreHistory = messages.length >= 50;
      });
    } catch (err) {
      this.log("history", id, err);
      this.store.update((s) => {
        const c = s.channels.get(id);
        if (c) c.failed = true;
      });
      // A refusal stays one; anything else, such as the server restarting,
      // is tried again, by one retry at a time.
      if ((!(err instanceof ApiError) || err.status >= 500 || err.status === 429) && !this.retrying.has(id)) {
        this.retrying.add(id);
        this.schedule(() => {
          this.retrying.delete(id);
          void this.loadHistory(id, attempt + 1);
        }, Math.min(30_000, 1000 * 2 ** attempt));
      }
    } finally {
      this.loading.delete(id);
    }
  }

  private loadOpen() {
    for (const id of this.open) void this.loadHistory(id);
  }

  async loadOlder(id: UUID, limit = 50) {
    const cs = this.store.getSnapshot().channels.get(id);
    if (!cs?.loaded || !cs.hasMoreHistory) return;
    const oldest = Math.min(...[...cs.messages.values()].map((m) => m.seq));
    const { messages } = await this.api.history(id, oldest, limit);
    this.store.update((s) => {
      const c = s.channels.get(id);
      if (!c) return;
      for (const m of messages) if (!c.messages.has(m.id)) c.messages.set(m.id, m);
      c.hasMoreHistory = messages.length >= limit;
    });
  }

  // loadTo loads a channel's history back to a message linked to, so it can
  // be shown; it gives up after some thousands of messages.
  async loadTo(id: UUID, messageID: UUID) {
    const has = () => this.store.getSnapshot().channels.get(id)?.messages.has(messageID);
    if (has()) return;
    const { message } = await this.api.message(id, messageID);
    for (let i = 0; i < 25 && !has(); i++) {
      const cs = this.store.getSnapshot().channels.get(id);
      if (!cs?.hasMoreHistory) return;
      const oldest = Math.min(...[...cs.messages.values()].map((m) => m.seq));
      if (oldest <= message.seq) return;
      await this.loadOlder(id, 200);
    }
  }

  async openThread(channel: UUID, root: UUID) {
    const { root: r, replies } = await this.api.thread(channel, root);
    this.store.update((s) => {
      const m = new Map(s.threads.get(root) ?? []);
      for (const x of replies) m.set(x.id, x);
      s.threads.set(root, m);
      const c = s.channels.get(channel);
      if (c?.loaded) c.messages.set(r.id, { ...c.messages.get(r.id), ...r });
    });
  }

  // send posts optimistically: a pending copy shows at once and is replaced
  // by the message.created event with the same client_msg_id.
  async send(channel: UUID, text: string, opts: SendOptions = {}) {
    const threadRootID = opts.threadRootID ?? null;
    const fileIDs = opts.fileIDs ?? [];
    const clientMsgID = opts.clientMsgID ?? newID();
    this.store.update((s) =>
      s.pending.set(clientMsgID, { clientMsgID, channelID: channel, threadRootID, text, fileIDs, alsoInChannel: opts.alsoInChannel, status: "sending", createdAt: this.now() }),
    );
    try {
      const { message } = await this.api.post(channel, text, clientMsgID, threadRootID, fileIDs, opts.alsoInChannel);
      this.store.update((s) => {
        if (!s.pending.delete(clientMsgID)) return;
        // The response came before the event; show the message now. The
        // event will find it already there.
        const c = s.channels.get(channel);
        if (message.thread_root_id) s.threads.get(message.thread_root_id)?.set(message.id, message);
        if ((!message.thread_root_id || message.also_in_channel) && c?.loaded) c.messages.set(message.id, message);
      });
    } catch (err) {
      this.log("send", err);
      this.store.update((s) => {
        const p = s.pending.get(clientMsgID);
        if (p) p.status = "failed";
      });
    }
  }

  // discard drops a message that could not be sent.
  discard(clientMsgID: string) {
    this.store.update((s) => {
      if (s.pending.get(clientMsgID)?.status === "failed") s.pending.delete(clientMsgID);
    });
  }

  // retry sends a failed message again with the same key, so it is never
  // posted twice.
  retry(clientMsgID: string) {
    const p = this.store.getSnapshot().pending.get(clientMsgID);
    if (p?.status === "failed") void this.send(p.channelID, p.text, { threadRootID: p.threadRootID, fileIDs: p.fileIDs, clientMsgID, alsoInChannel: p.alsoInChannel });
  }

  // held are channels marked unread while in view: seeing them does not
  // read them again until one leaves.
  private held = new Set<UUID>();

  // markRead reads a channel to its end; seen is set when it is read by
  // being in view, which leaves a channel marked unread as it is.
  async markRead(id: UUID, seen = false) {
    if (seen && this.held.has(id)) return;
    this.held.delete(id);
    const cs = this.store.getSnapshot().channels.get(id);
    const mem = cs?.channel.membership;
    if (!cs || !mem || (mem.last_read_seq >= cs.lastSeq && mem.unread_count === 0)) return;
    const seq = cs.lastSeq;
    this.store.update((s) => {
      const m = s.channels.get(id)?.channel.membership;
      if (m) Object.assign(m, { last_read_seq: seq, unread_count: 0, mention_count: 0 });
    });
    try {
      await this.api.markRead(id, seq);
    } catch (err) {
      this.log("mark read", err);
    }
  }

  // markUnread makes a channel unread from the message at seq on; it stays
  // so while in view, until release.
  async markUnread(id: UUID, seq: number) {
    this.held.add(id);
    const { channel } = await this.api.markUnread(id, seq).then(() => this.api.getChannel(id));
    this.store.update((s) => {
      const c = s.channels.get(id);
      if (c && channel.membership) c.channel.membership = channel.membership;
    });
  }

  // release lets seeing a channel read it again, as when one leaves it.
  release(id: UUID) {
    this.held.delete(id);
  }

  // watchPresence asks for these people's presence, now and after every
  // reconnect; the server tells a session only about those it watches.
  watchPresence(ids: UUID[]) {
    this.watched = [...new Set(ids)].slice(0, maxPresenceWatch);
    if (this.store.getSnapshot().connection === "online") this.sendWatch();
  }

  // setPresence tells others whether the user is active or away.
  setPresence(status: "active" | "away") {
    this.sendFrame("presence", { status });
  }

  // typing tells the channel's other members, at most every 3 seconds.
  typing(channel: UUID) {
    const last = this.lastTyping.get(channel) ?? 0;
    if (this.now() - last < typingSendMs) return;
    this.lastTyping.set(channel, this.now());
    this.sendFrame("typing", { channel_id: channel });
  }

  // declineHuddle tells the channel one will not join the call one was rung
  // for.
  declineHuddle(channel: UUID) {
    this.sendFrame("huddle_decline", { channel_id: channel });
  }
}

function newChannelState(ch: Channel): ChannelState {
  return { channel: ch, lastSeq: ch.last_seq, loaded: false, hasMoreHistory: true, messages: new Map() };
}

function findReply(threads: Map<UUID, Map<UUID, Message>>, id: UUID): Message | undefined {
  for (const t of threads.values()) {
    const m = t.get(id);
    if (m) return m;
  }
  return undefined;
}

interface Delta {
  offset: number;
  text: string;
}

// applyDelta puts streamed text into a streaming message where it follows
// on from what it holds. Offsets count code points, as every language can.
function applyDelta(m: Message, d: Delta) {
  const chars = Array.from(m.text);
  if (m.state === "streaming" && d.offset <= chars.length) m.text = chars.slice(0, d.offset).join("") + d.text;
}

function patchMessage(cs: ChannelState, threads: Map<UUID, Map<UUID, Message>>, id: UUID, fn: (m: Message) => void) {
  const m = cs.messages.get(id);
  if (m) {
    const copy = { ...m };
    fn(copy);
    cs.messages.set(id, copy);
  }
  for (const t of threads.values()) {
    const r = t.get(id);
    if (r) {
      const copy = { ...r };
      fn(copy);
      t.set(id, copy);
    }
  }
}

export function applyReaction(list: Reaction[], emoji: string, who: UUID, add: boolean): Reaction[] {
  const out = list.map((r) => ({ ...r, principal_ids: [...r.principal_ids] }));
  const r = out.find((x) => x.emoji === emoji);
  if (add) {
    if (!r) return [...out, { emoji, count: 1, principal_ids: [who] }];
    if (!r.principal_ids.includes(who)) {
      r.principal_ids.push(who);
      r.count++;
    }
    return out;
  }
  if (!r) return out;
  r.principal_ids = r.principal_ids.filter((p) => p !== who);
  r.count = Math.max(0, r.count - 1);
  return out.filter((x) => x.count > 0);
}

function typingStopped(typing: Map<UUID, Map<UUID, number>>, channel: UUID, who: UUID) {
  typing.get(channel)?.delete(who);
}

// newID is a random (v4) UUID. crypto.randomUUID exists only in secure
// contexts, and Lantern is also served over plain HTTP on a LAN, so fall
// back to getRandomValues, which works everywhere.
export function newID(): string {
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  const b = crypto.getRandomValues(new Uint8Array(16));
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const h = [...b].map((x) => x.toString(16).padStart(2, "0")).join("");
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

function deviceID(): string {
  try {
    let id = localStorage.getItem("lantern.device_id");
    if (!id) {
      id = newID();
      localStorage.setItem("lantern.device_id", id);
    }
    return id;
  } catch {
    return "unknown";
  }
}
